import CurrencyView from '@app/components/price/currency-view.tsx';
import { getFragmentData } from '@app/graphql/types';
import {
  BranchItemFragment,
  DaysOfWeek,
  MenuProductItemFragment,
  MenuSectionItemFragment,
  ProductConfigurationType,
} from '@app/graphql/types/graphql.ts';
import {
  MenuProductConfigurationFragment,
  MenuProductConfigurationValueFragment,
} from '@app/page/online-order/model/menu-product-fragment.ts';
import { analyticProductDetailClick } from '@app/page/online-order/product-picker/utils/online-order-analytic.ts';
import { SortType } from '@app/page/online-order/product-picker/utils/sort-type.ts';
import { naturalCompare } from '@app/utils/natural-compare.ts';
import { Image, Box, Card, Collapsible, Flex, HStack, Heading, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { useTranslate } from '@tolgee/react';
import moment, { Moment } from 'moment-timezone';
import ProductConfigurationSetupV2 from './product-configuration-setup-chakra';
import ProductLabelIcon from './product-label-icon';
import CDNLink from '../../../../utils/cdn-link';

export default function ProductPickerList(props: {
  sections: readonly MenuSectionItemFragment[];
  products: readonly MenuProductItemFragment[];
  timezone: string;
  sortType: SortType;
  branch?: BranchItemFragment;
}) {
  const sections = [...props.sections].sort((a, b) => naturalCompare(a.code, b.code));
  const { t } = useTranslate();
  const now = moment.tz(props.timezone);
  const dayOfWeek = currentDayOfWeek(now);
  const currentHour = now.format('HH:mm');

  return (
    <Card.Root className="product-picker card" bgColor={'white'} border={'none'} rounded={'none'} padding={{ base: 0, lg: 6 }}>
      <Card.Body position={'relative'} p={0}>
        <VStack className="product-sections" gapY={{ base: 0, lg: 8 }} align={'stretch'}>
          {sections.map((section) => {
            const products = props.products
              .filter((product) => product.sectionId === section.id && product.enabled)
              .sort((a, b) => a.title.localeCompare(b.title));

            if (products.length === 0) return null;

            // Check if any product in this section has a description
            const sectionHasDescriptions = products.some(
              (product) => product.description && typeof product.description === 'string' && product.description.trim().length > 0
            );
            console.log(`Section "${section.name}" has descriptions:`, sectionHasDescriptions);

            const checkSectionEnable = section.enabled;

            if (!checkSectionEnable) {
              return null;
            }

            let checkSectionAvailable = false;

            if (section.timeRule.__typename == 'MenuSectionAlwaysTimeRule') {
              checkSectionAvailable = true;
            }

            if (section.timeRule.__typename == 'MenuSectionRangeTimeRule') {
              const sectionAvailableDayOfWeek = section.timeRule.daysOfWeek ?? [];
              const sectionEndHour = section.timeRule.end;

              if (sectionAvailableDayOfWeek.includes(dayOfWeek)) {
                if (currentHour <= sectionEndHour) {
                  checkSectionAvailable = true;
                }
              }
            }

            if (!section.inStock) {
              checkSectionAvailable = false;
            }

            return (
              <Collapsible.Root
                className="product-section"
                defaultOpen
                unmountOnExit
                id={section.id}
                key={section.id}
                color="white"
                mt={{ base: 6, lg: 0 }}
              >
                <Collapsible.Trigger className="product-section__header" w={'full'} color={'gray.600'} mx={2} mb={4}>
                  <Flex
                    width={'full'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    paddingX={{ base: 4, lg: 0 }}
                  >
                    {/* Keep that box component, it's use for category selection */}
                    <Flex direction={'column'} alignItems={'start'}>
                      <Heading className="product-section__title" textAlign={'start'} fontSize="xl" fontWeight="bold">
                        <HStack gap={2} align="center">
                          <Box id={section.id}> {section.name}</Box>

                          {!section.inStock && (
                            <Text
                              color={'white'}
                              fontSize={'sm'}
                              fontWeight={'semibold'}
                              bgColor={'red.500'}
                              px={2}
                              py={0.5}
                              border={'1px solid'}
                              rounded={'xl'}
                              lineHeight="1.5"
                            >
                              {t('online-order.category.instock', 'Sold out')}
                            </Text>
                          )}
                        </HStack>
                      </Heading>

                      {section.timeRule.__typename == 'MenuSectionRangeTimeRule' && (
                        <Text fontSize="sm" fontWeight="semibold" color="red.600" letterSpacing="wider">
                          {t('open', 'Open')}{' '}
                          {!section.timeRule.isAllDays && (
                            <>
                              {t(
                                'online-order.picker-product.section.range-time.description-1',
                                'from {start} to {end}',
                                {
                                  start: section.timeRule.start,
                                  end: section.timeRule.end,
                                }
                              )}
                            </>
                          )}
                          <>
                            {t('online-order.picker-product.section.range-time.description-2', ' on {days}', {
                              days: section.timeRule.daysOfWeek
                                ?.map((day) => day.slice(0, 3))
                                .join(', ')
                                .toLowerCase(),
                            })}
                          </>
                        </Text>
                      )}

                      <Text textAlign={'start'}>{section.description}</Text>
                    </Flex>

                    <ChevronDownIcon
                      style={{
                        width: 30,
                        height: 30,
                      }}
                    />
                  </Flex>
                </Collapsible.Trigger>

                <Collapsible.Content className="product-section__content">
                  <SimpleGrid
                    className="product-grid"
                    columns={{ base: 1, lg: 2 }}
                    gap={{ lg: 4 }}
                    opacity={!checkSectionAvailable ? 0.5 : 1}
                    pointerEvents={!checkSectionAvailable ? 'none' : 'auto'}
                  >
                    {products
                      .map((product) => {
                        const configurations = product.configurations.map((config) => {
                          const configuration = getFragmentData(MenuProductConfigurationFragment, config);

                          return {
                            ...configuration,
                            values: configuration.values.map((value) => {
                              return getFragmentData(MenuProductConfigurationValueFragment, value);
                            }),
                          };
                        });

                        const singlePrice = configurations.length === 1 && configurations[0].values.length === 1;
                        const basePrices =
                          configurations
                            .find((config) => config.type === ProductConfigurationType.Base)
                            ?.values.map((value) => value.price) ?? [];

                        const minBasePrice = Math.min(...basePrices);

                        return {
                          ...product,
                          minBasePrice,
                          singlePrice,
                        };
                      })
                      .sort((a, b) => {
                        if (props.sortType == SortType.INCREASE) return a.minBasePrice - b.minBasePrice;
                        if (props.sortType == SortType.DECREASE) return b.minBasePrice - a.minBasePrice;
                        return naturalCompare(a.code, b.code);
                      })
                      .map((product) => {
                        return (
                          <Box
                            className="product-item card"
                            key={product.id}
                            paddingX={'4px'}
                            rounded={{ lg: 'xl' }}
                            boxShadow={{
                              lg: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
                            }}
                            bgColor={'white'}
                            border={'1px solid'}
                            borderColor="gray.300"
                            opacity={!product.inStock ? 0.5 : 1}
                            pointerEvents={!product.inStock || !section.inStock ? 'none' : 'auto'}
                          >
                            <ProductConfigurationSetupV2
                              branch={props.branch}
                              product={product}
                              onClick={() => {
                                analyticProductDetailClick({
                                  productName: product.title,
                                });
                              }}
                            >
                              <Flex className="product-item__content" justify="space-between" py={3} px={{ base: 5, lg: 3 }} align={'center'}>
                                <HStack justifyContent={'space-between'} w="full">
                                  <VStack className="product-item__info" align="start" gap={2} flex={1}>
                                    <HStack className="product-item__header" wrap={'wrap'} gap={2} align="center">
                                      <Text className="product-item__title" fontSize="lg" fontWeight="medium" color="gray.800">
                                        {product.code}. {product.title}
                                      </Text>
                                      {product.labels && product.labels.length > 0 && (
                                        <HStack className="product-item__labels" gap={1}>
                                          {product.labels.map((label) => (
                                            <ProductLabelIcon key={label} label={label} />
                                          ))}
                                        </HStack>
                                      )}
                                      {!product.inStock && (
                                        <Text
                                          className="product-item__badge product-item__badge--sold-out"
                                          color={'white'}
                                          fontSize={'sm'}
                                          fontWeight={'semibold'}
                                          bgColor={'red.500'}
                                          px={2}
                                          py={0.5}
                                          border={'1px solid'}
                                          rounded={'xl'}
                                          lineHeight="1.5"
                                        >
                                          {t('online-order.category.instock', 'Sold out')}
                                        </Text>
                                      )}
                                    </HStack>

                                    {sectionHasDescriptions && (
                                      <Box className="product-item__description-container" h="2.5rem" overflow="hidden" mt={-1}>
                                        {product.description && (
                                          <Text
                                            className="product-item__description"
                                            fontSize="sm"
                                            color="gray.500"
                                            lineClamp={2}
                                          >
                                            {product.description}
                                          </Text>
                                        )}
                                      </Box>
                                    )}

                                    {product.allergics.length > 0 && (
                                      <Text className="product-item__allergics" fontSize="xs" color="gray.400" lineClamp={2}>
                                        {t('product.allergy', 'Allergy')}:{' '}
                                        {product.allergics.map((item) => item.title).join(', ')}
                                      </Text>
                                    )}

                                    <Text className="product-item__price" fontSize="sm" fontWeight="semibold" color="gray.600">
                                      {product.singlePrice ? (
                                        <CurrencyView price={product.minBasePrice} />
                                      ) : (
                                        <Box>
                                          {t('online-order.picker-page.product-list-style.start-from', 'Start from')}{' '}
                                          <CurrencyView price={product.minBasePrice} />
                                        </Box>
                                      )}
                                    </Text>
                                  </VStack>

                                  <Image
                                    className="product-item__image"
                                    src={CDNLink(product.images.at(0) ?? props.branch?.company.logo ?? '')}
                                    rounded="xl"
                                    alt="logo"
                                    objectFit="cover"
                                    width="24"
                                    height="24"
                                  />
                                </HStack>
                              </Flex>
                            </ProductConfigurationSetupV2>
                          </Box>
                        );
                      })}
                  </SimpleGrid>
                </Collapsible.Content>
              </Collapsible.Root>
            );
          })}
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}

function currentDayOfWeek(date: Moment): DaysOfWeek {
  switch (date.day()) {
    case 0:
      return DaysOfWeek.Sunday;
    case 1:
      return DaysOfWeek.Monday;
    case 2:
      return DaysOfWeek.Tuesday;
    case 3:
      return DaysOfWeek.Wednesday;
    case 4:
      return DaysOfWeek.Thursday;
    case 5:
      return DaysOfWeek.Friday;
    case 6:
      return DaysOfWeek.Saturday;
  }

  throw new Error('Invalid day of week');
}
